import { useApi } from '@hooks/useApi';
import { Product, ProductTenor } from '@protos/product';
import { TradeLimit } from '@protos/user';
import { OtcExchange, OtcOrderType } from '@protos/v2/otcQuote';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { useTradableProductSymbolsContext } from '@shared/contexts/TradableProductSymbolsProvider';
import { useTradingTenorContext } from '@shared/contexts/TradingTenorProvider';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_TRADE_SIZE } from '../constants';
import { TradingPanel } from '../TradingContext';

interface UseTradingFormProps {
  id: string;
  payload: TradingPanel;
  userTradeLimits: TradeLimit[];
  tenors: ProductTenor[];
  defaultTradeSizes: Record<string, number>;
}

export const canProductTradeAsLimit = (userTradeLimits: TradeLimit[], product: Product | undefined) => {
  if (!product) return false;
  if (userTradeLimits.length === 0) return false;
  return userTradeLimits.some(
    limit => limit.symbol.toLowerCase() === product?.symbol?.toLowerCase() && limit.order_types.includes(OtcOrderType.limit)
  );
};

export const useTradingForm = ({ id, payload, userTradeLimits, tenors, defaultTradeSizes }: UseTradingFormProps) => {
  const { apiClient } = useApi();
  const { editWidgetPayloadById } = useDashboardContext();
  const { getAllUsedTenors } = useTradingTenorContext();
  const tradableProducts = useTradableProductSymbolsContext();
  const [isLoadingPanel, setIsLoadingPanel] = useState<boolean>(true);
  const [isTradeLimitBreached, setIsTradeLimitBreached] = useState<boolean>(false);
  const [validTenors, setValidTenors] = useState<ProductTenor[]>([]);
  const [validBackTenors, setValidBackTenors] = useState<ProductTenor[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [underlyingProduct, setUnderlyingProduct] = useState<Product | undefined>(undefined);
  const [tenorsForProduct, setTenorsForProduct] = useState<Set<string>>(new Set());
  const [tenorsNeedUpdate, setTenorsNeedUpdate] = useState(false);
  const [isLockedBuy, setIsLockedBuy] = useState<boolean>(payload.isLockedBuy || false);
  const [isLockedSell, setIsLockedSell] = useState<boolean>(payload.isLockedSell || false);
  const [lockedBuyPrice, setLockedBuyPrice] = useState<string | undefined>(payload.lockedBuyPrice);
  const [lockedSellPrice, setLockedSellPrice] = useState<string | undefined>(payload.lockedSellPrice);
  const [minTrade, setMinTrade] = useState<number>(0);
  const [isValidExchange, setIsValidExchange] = useState<boolean>(true);
  const [isQuantityUnitSpecified, setIsQuantityUnitSpecified] = useState<boolean>(false);

  const {
    selectedProduct: incomingSelectedProduct,
    selectedTenor: incomingSelectedTenor,
    selectedTradingAccount: incomingSelectedTradingAccount,
    selectedExchange: incomingSelectedExchange,
  } = payload;

  const allowedTradingProducts = useMemo(
    () => userTradeLimits.map(limit => limit.symbol).filter(symbol => tradableProducts.allSymbols.includes(symbol)),
    [userTradeLimits, tradableProducts]
  );

  const availableTradingTenors = useMemo(() => {
    if (tenorsForProduct.size === 0 || !selectedProduct) return [];
    return tenors.filter(t => tenorsForProduct.has(t.code) && t.frequency === selectedProduct.tenor_frequency);
  }, [tenorsForProduct, tenors, selectedProduct]);

  const initialSelectedTenor = useMemo(() => {
    if (!availableTradingTenors.length) return '';

    // Get currently used tenors for the default product and exchange
    const defaultExchange = userTradeLimits?.[0]?.exchange?.[0];
    const defaultProduct = allowedTradingProducts[0];
    const allUsedTenors = getAllUsedTenors(id, false, defaultExchange as OtcExchange, defaultProduct);

    const availableTenor = availableTradingTenors.find(tenor => !allUsedTenors.find(usedTenor => (usedTenor as ProductTenor)?.code === tenor?.code));

    return availableTenor?.code || availableTradingTenors[0]?.code;
  }, [getAllUsedTenors, availableTradingTenors, incomingSelectedExchange, id, allowedTradingProducts, userTradeLimits, tenorsForProduct]);

  let transformedPayloadTenor: string | { left: string; right: string } | undefined;
  if (availableTradingTenors.find(tenor => tenor?.code === incomingSelectedTenor)) {
    transformedPayloadTenor = incomingSelectedTenor;
  } else if (incomingSelectedTenor && typeof incomingSelectedTenor === 'object') {
    transformedPayloadTenor = {
      left: incomingSelectedTenor.left,
      right: incomingSelectedTenor.right || availableTradingTenors[0]?.code,
    };
  } else {
    transformedPayloadTenor = availableTradingTenors[0]?.code;
  }

  const defaultProduct = useMemo(() => {
    if (userTradeLimits.find(limit => limit.symbol === incomingSelectedProduct) && allowedTradingProducts.includes(incomingSelectedProduct)) {
      return incomingSelectedProduct;
    }
    return allowedTradingProducts[0];
  }, [allowedTradingProducts, incomingSelectedProduct, userTradeLimits]);

  const [tradingForm, setTradingForm] = useState<TradingPanel>({
    selectedProduct: defaultProduct,
    selectedTenor: transformedPayloadTenor || initialSelectedTenor,
    tradeSize: payload.tradeSize || defaultTradeSizes[defaultProduct] || DEFAULT_TRADE_SIZE,
    selectedTradingAccount: payload.selectedTradingAccount,
    selectedExchange: payload.selectedExchange || userTradeLimits?.[0]?.exchange?.[0],
    selectedOrderType: payload.selectedOrderType || OtcOrderType.fill_or_kill,
    isLockedBuy: payload.isLockedBuy || false,
    isLockedSell: payload.isLockedSell || false,
    lockedBuyPrice: payload.lockedBuyPrice,
    lockedSellPrice: payload.lockedSellPrice,
  });

  const productTradingLimit = useMemo(
    () => userTradeLimits.find(limit => limit.symbol === defaultProduct?.toLowerCase())?.limit ?? 0,
    [userTradeLimits, incomingSelectedProduct]
  );

  const showUserInputError = useMemo(() => {
    const { selectedProduct, selectedTenor, selectedExchange, tradeSize } = tradingForm;
    const isValidTradeSize = tradeSize > 0 && Number.isInteger(tradeSize) && !isTradeLimitBreached;

    return !selectedProduct || !selectedTenor || !selectedExchange || !isValidTradeSize;
  }, [tradingForm]);

  const getExchangeFromProduct = useCallback(
    (product?: Product) => {
      return product?.exchange_symbols?.find(ex => ex.exchange === tradingForm.selectedExchange);
    },
    [tradingForm.selectedExchange]
  );

  const rawTradingAmount = useMemo(() => {
    if (!selectedProduct || !tradingForm.tradeSize) return undefined;
    const exchangeFromProduct = getExchangeFromProduct(selectedProduct);
    const exchangeFromUnderlying = getExchangeFromProduct(underlyingProduct);

    let contractSize = 0;
    if (exchangeFromProduct?.contract_size) {
      contractSize = Number(exchangeFromProduct?.contract_size);
    } else if (exchangeFromUnderlying?.contract_size) {
      contractSize = Number(exchangeFromUnderlying?.contract_size);
    }

    const rawAmount = tradingForm.tradeSize * contractSize;
    let quantityUnit = 'unspecified';
    if (exchangeFromProduct?.quantity_unit) {
      quantityUnit = exchangeFromProduct?.quantity_unit;
    } else if (exchangeFromUnderlying?.quantity_unit) {
      quantityUnit = exchangeFromUnderlying?.quantity_unit;
    }
    setIsQuantityUnitSpecified(quantityUnit !== 'unspecified');
    return `${rawAmount.toLocaleString()} ${quantityUnit !== 'unspecified' ? quantityUnit?.toUpperCase() : ''}`;
  }, [selectedProduct, tradingForm.selectedExchange, tradingForm.tradeSize, underlyingProduct]);

  useEffect(() => {
    const fetchProduct = async () => {
      const prod = await apiClient?.getProductBySymbol(tradingForm.selectedProduct);
      if (prod) {
        setSelectedProduct(prod);
        if (prod.calendar_type === 'spread') {
          setTenorsForProduct(
            new Set(
              prod.contracts.flatMap(c => {
                const mid = c.tenor_code.length / 2;
                return [c.tenor_code.slice(0, mid), c.tenor_code.slice(mid)];
              })
            )
          );
        } else {
          if (prod.product_type === 'moc') {
            setTenorsForProduct(new Set(prod.contracts.map(c => c.tenor_code).slice(0, 3)));
          } else {
            setTenorsForProduct(new Set(prod.contracts.map(c => c.tenor_code)));
          }
        }

        if (tradingForm.selectedOrderType === OtcOrderType.limit) {
          if (!canProductTradeAsLimit(userTradeLimits, prod)) {
            setTradingForm({ ...tradingForm, selectedOrderType: OtcOrderType.fill_or_kill });
          }
        }
      }
      if (prod?.calendar_type === 'spread' && prod?.exchange_symbols.length === 0) {
        const underlying = await apiClient?.getProductBySymbol(prod.underlying_symbol);
        if (underlying) setUnderlyingProduct(underlying);
      } else {
        setUnderlyingProduct(undefined);
      }
    };

    if (apiClient && tradingForm.selectedProduct !== selectedProduct?.symbol) {
      fetchProduct();
    }
  }, [apiClient, tradingForm.selectedProduct]);

  useEffect(() => {
    if (selectedProduct && tradingForm.selectedExchange) {
      const exchangeInfo = getExchangeFromProduct(selectedProduct);
      const underlyingExchangeInfo = getExchangeFromProduct(underlyingProduct);

      if (!exchangeInfo && !underlyingExchangeInfo) {
        setIsValidExchange(false);
        setMinTrade(0);
        return;
      }

      setIsValidExchange(true);

      let newMinTrade = 0;

      if (exchangeInfo) {
        newMinTrade = Number(exchangeInfo.min_size);
      } else if (underlyingExchangeInfo) {
        newMinTrade = Number(underlyingExchangeInfo.min_size);
      }

      // If they're sat at the previous minimum trade size, move them to the actual
      // minimum
      if (minTrade === tradingForm.tradeSize || tradingForm.tradeSize === DEFAULT_TRADE_SIZE) {
        setTradingForm({ ...tradingForm, tradeSize: newMinTrade });
      }

      setMinTrade(newMinTrade);
    } else {
      setIsValidExchange(true);
    }
  }, [selectedProduct, underlyingProduct, tradingForm.selectedExchange]);

  useEffect(() => {
    if (incomingSelectedProduct && defaultTradeSizes[incomingSelectedProduct] && tradingForm.tradeSize === DEFAULT_TRADE_SIZE) {
      setTradingForm(prev => ({
        ...prev,
        tradeSize: defaultTradeSizes[incomingSelectedProduct],
      }));
    }
  }, [incomingSelectedProduct, defaultTradeSizes, tradingForm.tradeSize]);

  const onTradingProductChange = useCallback(
    (value: string) => {
      if (value === defaultProduct) return;
      if (!allowedTradingProducts.includes(value)) return;
      setIsLoadingPanel(true);
      setTenorsNeedUpdate(true);

      setTradingForm(prev => ({
        ...prev,
        tradeSize: defaultTradeSizes[value] || DEFAULT_TRADE_SIZE,
        selectedProduct: value,
        isLockedBuy: false,
        isLockedSell: false,
        lockedBuyPrice: undefined,
        lockedSellPrice: undefined,
      }));
      onExchangeChange(userTradeLimits.find(limit => limit.symbol === value)?.exchange?.[0] as OtcExchange);
      setIsLockedBuy(false);
      setIsLockedSell(false);
      setLockedBuyPrice(undefined);
      setLockedSellPrice(undefined);

      editWidgetPayloadById(id, {
        ...payload,
        selectedProduct: value,
        tradeSize: defaultTradeSizes[value] || DEFAULT_TRADE_SIZE,
        isLockedBuy: false,
        isLockedSell: false,
        lockedBuyPrice: undefined,
        lockedSellPrice: undefined,
      });
    },
    [tradingForm, allowedTradingProducts, defaultProduct, payload]
  );

  const updateInitialTenors = useCallback(
    (tenorsList: ProductTenor[]) => {
      if (!selectedProduct) return;

      const isNewProductSpread = selectedProduct.calendar_type === 'spread';
      const exchange =
        incomingSelectedExchange ||
        (userTradeLimits.find(limit => limit.symbol === selectedProduct.symbol)?.exchange?.[0] as OtcExchange) ||
        (userTradeLimits?.[0]?.exchange?.[0] as OtcExchange);
      const allUsedTenors = getAllUsedTenors(id, isNewProductSpread, exchange, selectedProduct.symbol);

      if (isNewProductSpread) {
        const leftTenorCode = tenorsList[0]?.code;
        const usedSpreadProductTenors = (allUsedTenors as { left: ProductTenor; right: ProductTenor }[]).filter(t => t.left?.code === leftTenorCode);
        let rightTenorCode = tenorsList[1]?.code;

        if (usedSpreadProductTenors.length) {
          const usedRightTenors = usedSpreadProductTenors.map(t => t.right?.code);
          const availableRightTenors = tenorsList.filter(tenor => !usedRightTenors.includes(tenor?.code) && tenor?.code !== leftTenorCode);
          rightTenorCode = availableRightTenors[0]?.code;
        }

        setTradingForm(prev => ({
          ...prev,
          selectedTenor: { left: leftTenorCode, right: rightTenorCode },
        }));
      } else {
        const availableTenors = tenorsList.filter(tenor => !allUsedTenors.find(usedTenor => (usedTenor as ProductTenor)?.code === tenor?.code));
        const incomingTenorIsValid = incomingSelectedTenor && availableTenors.find(tenor => tenor.code === incomingSelectedTenor);
        setTradingForm(prev => ({
          ...prev,
          selectedTenor: incomingTenorIsValid ? incomingSelectedTenor : availableTenors[0]?.code,
        }));
      }
      setTenorsNeedUpdate(false);
    },
    [tradingForm, selectedProduct, incomingSelectedExchange, getAllUsedTenors, id, userTradeLimits, incomingSelectedTenor]
  );

  const getAvailableRightTenors = useCallback(
    (leftTenorCode: string, usedRightTenors: string[]) => {
      const indexOfLeftTenor = availableTradingTenors.findIndex(tenor => tenor?.code === leftTenorCode);
      return availableTradingTenors.filter(
        (tenor, index) => !usedRightTenors.includes(tenor?.code) && tenor?.code !== leftTenorCode && index > indexOfLeftTenor
      );
    },
    [availableTradingTenors]
  );

  const onTenorChange = useCallback(
    (value: string, key?: 'left' | 'right') => {
      setIsLoadingPanel(true);
      const { selectedTenor, selectedExchange } = tradingForm;
      const isSpreadTenorChange = key !== undefined;

      if (!isSpreadTenorChange) {
        setTradingForm((prev: TradingPanel) => ({ ...prev, selectedTenor: value }));
      } else {
        if (typeof selectedTenor !== 'object') {
          //currently selected tenor is not a spread, so make it a spread
          const newTenor = { left: value, right: availableTradingTenors[1]?.code };
          setTradingForm((prev: TradingPanel) => ({ ...prev, selectedTenor: newTenor }));
        } else if (selectedTenor[key] === value) {
          return;
        } else if (key === 'right') {
          const newTenor = { ...selectedTenor, right: value };
          setTradingForm((prev: TradingPanel) => ({ ...prev, selectedTenor: newTenor }));
        } else {
          const allUsedTenors = getAllUsedTenors(id, true, selectedExchange, incomingSelectedProduct);
          const usedSpreadProductTenors = (allUsedTenors as { left: ProductTenor; right: ProductTenor }[]).filter(t => t.left?.code === value);
          const updatedRightTenors = usedSpreadProductTenors.map(t => t.right?.code);
          const availableRightTenors = getAvailableRightTenors(value, updatedRightTenors);
          const newTenor = { left: value, right: availableRightTenors[0]?.code };
          setTradingForm((prev: TradingPanel) => ({ ...prev, selectedTenor: newTenor }));
        }
      }
    },
    [tradingForm, getAllUsedTenors, availableTradingTenors]
  );

  const onExchangeChange = useCallback(
    (value: OtcExchange) => {
      setIsLoadingPanel(true);
      setTradingForm((prev: TradingPanel) => ({ ...prev, selectedExchange: value }));
    },
    [tradingForm]
  );

  const onTradeSizeChange = useCallback(
    debounce((_, newValue) => {
      setTradingForm((prev: TradingPanel) => ({ ...prev, tradeSize: newValue }));

      if (newValue > productTradingLimit) {
        setIsTradeLimitBreached(true);
      } else if (isTradeLimitBreached) {
        setIsTradeLimitBreached(false);
      }
    }, 350),
    [isTradeLimitBreached, productTradingLimit]
  );

  useEffect(() => {
    // Available tenors changed, update initial ones if needed
    if (tenorsNeedUpdate || !tradingForm.selectedTenor) updateInitialTenors(availableTradingTenors);
  }, [availableTradingTenors]);

  useEffect(() => {
    if (incomingSelectedTradingAccount) {
      setTradingForm((prev: TradingPanel) => ({ ...prev, selectedTradingAccount: incomingSelectedTradingAccount }));
      editWidgetPayloadById(id, {
        ...payload,
        selectedTradingAccount: incomingSelectedTradingAccount,
      });
    }
  }, [incomingSelectedTradingAccount]);

  const onToggleBuyLock = useCallback(
    (price?: string) => {
      setIsLockedBuy(prevLockedState => {
        const newLockedState = !prevLockedState;
        setLockedBuyPrice(newLockedState ? price : undefined);

        setTradingForm(prev => ({
          ...prev,
          isLockedBuy: newLockedState,
          lockedBuyPrice: newLockedState ? price : undefined,
        }));

        editWidgetPayloadById(id, {
          ...payload,
          isLockedBuy: newLockedState,
          lockedBuyPrice: newLockedState ? price : undefined,
        });

        return newLockedState;
      });
    },
    [editWidgetPayloadById, id, payload]
  );

  const onToggleSellLock = useCallback(
    (price?: string) => {
      setIsLockedSell(prevLockedState => {
        const newLockedState = !prevLockedState;
        setLockedSellPrice(newLockedState ? price : undefined);

        setTradingForm(prev => ({
          ...prev,
          isLockedSell: newLockedState,
          lockedSellPrice: newLockedState ? price : undefined,
        }));

        editWidgetPayloadById(id, {
          ...payload,
          isLockedSell: newLockedState,
          lockedSellPrice: newLockedState ? price : undefined,
        });

        return newLockedState;
      });
    },
    [editWidgetPayloadById, id, payload]
  );

  const onOrderTypeChange = (value: OtcOrderType) => {
    setTradingForm((prev: TradingPanel) => ({ ...prev, selectedOrderType: value, isLockedBuy: false, isLockedSell: false }));
  };

  return {
    tradingForm,
    rawTradingAmount,
    isLoadingPanel,
    tenorsNeedUpdate,
    isTradeLimitBreached,
    showUserInputError,
    allowedTradingProducts,
    availableTradingTenors,
    validTenors,
    validBackTenors,
    onTradingProductChange,
    onTenorChange,
    onExchangeChange,
    onTradeSizeChange,
    setIsLoadingPanel,
    setValidTenors,
    setValidBackTenors,
    getAvailableRightTenors,
    isLockedBuy,
    isLockedSell,
    lockedBuyPrice,
    lockedSellPrice,
    onToggleBuyLock,
    onToggleSellLock,
    onOrderTypeChange,
    isBelowMinTrade: tradingForm.tradeSize < minTrade,
    minTrade,
    isValidExchange,
    isQuantityUnitSpecified,
  };
};
